/* =========================================================
   Custom Smile.io rewards launcher
   - On click, toggles the official rewards panel via Smile UI's openPanel() / closePanel()
   - Smile UI (launcher + panel) is injected and already initialized by the
     official Smile app, so we do NOT bundle smile-ui.js here, nor call
     SmileUI.initialize(); we only wait for window.SmileUI then trigger openPanel()
   - If Smile UI never loads (app uninstalled), hide the launcher to avoid a dead entry point
   ========================================================= */
(function () {
  var SELECTOR = '.wt-smile-launcher';
  /* SmileUI renders the rewards panel into #smile-ui-container in the light DOM (no shadow
     root — verified against the live store), so the panel frame is reachable from here. */
  var PANEL_SELECTOR = '#smile-ui-container .smile-panel-frame-container';
  var POLL_MS = 250;
  var MAX_WAIT_MS = 10000;

  // Return SmileUI only when it is ready and openPanel exists
  function getSmile() {
    var smile = window.SmileUI;
    return smile && typeof smile.openPanel === 'function' ? smile : null;
  }

  // Poll until Smile UI is injected; once ready, use SmileUI.ready() to await customer identification
  function whenReady(callback) {
    var startedAt = Date.now();

    (function poll() {
      var smile = getSmile();

      if (smile) {
        if (typeof smile.ready === 'function') {
          smile.ready()
            .then(function () { callback(smile); })
            .catch(function () { /* Silently ignore initialization failure */ });
        } else {
          callback(smile);
        }
        return;
      }

      if (Date.now() - startedAt < MAX_WAIT_MS) {
        setTimeout(poll, POLL_MS);
      }
    })();
  }

  // SmileUI exposes no isOpen(), so the open state has to be read from the DOM: a rendered,
  // non-zero-sized panel frame means the rewards panel is currently open.
  function isPanelOpen() {
    var panel = document.querySelector(PANEL_SELECTOR);

    if (!panel) return false;

    var rect = panel.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // The launcher now behaves as a toggle, so it advertises its state.
  // The panel can also be closed from inside its own iframe, where no click ever reaches this
  // document — hence the MutationObserver below rather than syncing on our own clicks only.
  var panelWatched = false;

  function syncExpanded() {
    var open = isPanelOpen();

    document.querySelectorAll(SELECTOR).forEach(function (button) {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function watchPanelState() {
    if (panelWatched) return true;

    // #smile-ui-container is injected on demand, so it may not exist yet
    var container = document.getElementById('smile-ui-container');
    if (!container) return false;

    panelWatched = true;

    if (typeof MutationObserver === 'function') {
      new MutationObserver(syncExpanded).observe(container, { childList: true, subtree: true });
    }
    syncExpanded();
    return true;
  }

  function toggle(button) {
    // Hiding Smile's own launcher also removed the toggle behaviour it used to provide,
    // so restore it here: clicking again while the panel is open must close the panel.
    // The state is read at click time — not after awaiting Smile's ready() promise, which
    // can resolve seconds later and would otherwise toggle the wrong way.
    if (isPanelOpen()) {
      var smile = getSmile();

      if (smile && typeof smile.closePanel === 'function') {
        smile.closePanel();
      }
      return;
    }

    var deepLink = button.getAttribute('data-wt-deep-link');

    whenReady(function (readySmile) {
      readySmile.openPanel(deepLink ? { deep_link: deepLink } : undefined);
      watchPanelState();
    });
  }

  // Smile UI never appears → the app is not installed, hide the launcher
  function hideIfUnavailable() {
    var startedAt = Date.now();

    (function poll() {
      if (getSmile()) return;

      if (Date.now() - startedAt < MAX_WAIT_MS) {
        setTimeout(poll, POLL_MS);
        return;
      }

      document.querySelectorAll(SELECTOR).forEach(function (el) {
        el.classList.add('wt-smile-launcher--unavailable');
      });
    })();
  }

  // The official Smile app may inject its own floating launcher; hide it so only our themed one
  // shows. Works in the light DOM and in open shadow roots, and never hides the rewards panel.
  var APP_LAUNCHER = '.launcher-container';
  var SHADOW_HOSTS = 'smile-ui, smile-ui-lite, smile-launcher, smile-widget, [id*="smile" i], [class*="smile" i]';

  function hideAppLaunchers(root, depth) {
    if (!root || depth > 5) return;

    var found = root.querySelectorAll(APP_LAUNCHER);
    for (var i = 0; i < found.length; i++) {
      found[i].style.setProperty('display', 'none', 'important');
    }

    var hosts = root.querySelectorAll(SHADOW_HOSTS);
    for (var j = 0; j < hosts.length; j++) {
      if (hosts[j].shadowRoot) hideAppLaunchers(hosts[j].shadowRoot, depth + 1);
    }
  }

  // Smile UI is injected asynchronously, so keep sweeping while it loads
  function watchAppLauncher() {
    var startedAt = Date.now();

    (function poll() {
      hideAppLaunchers(document, 0);

      if (Date.now() - startedAt < MAX_WAIT_MS) {
        setTimeout(poll, POLL_MS);
      }
    })();
  }

  // Event delegation: handles launchers inserted dynamically by the theme editor
  document.addEventListener('click', function (event) {
    var target = event.target;
    var button = target && target.closest ? target.closest(SELECTOR) : null;

    if (!button) return;

    event.preventDefault();
    toggle(button);
  });

  // Entrance: reveal the launcher after an optional delay, once scrolled past a threshold, or right away
  function reveal(button) {
    if (!button.classList.contains('wt-smile-launcher--pre')) return;

    // Wait two frames so the browser paints the hidden state before we transition it away
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        button.classList.remove('wt-smile-launcher--pre');
      });
    });
  }

  function initAppear(button) {
    var mode = button.getAttribute('data-wt-appear') || 'immediate';

    if (mode === 'delay') {
      var seconds = parseFloat(button.getAttribute('data-wt-delay')) || 0;
      setTimeout(function () { reveal(button); }, Math.max(0, seconds * 1000));
      return;
    }

    if (mode === 'scroll') {
      var threshold = parseFloat(button.getAttribute('data-wt-scroll')) || 0;
      var onScroll = function () {
        if ((window.pageYOffset || document.documentElement.scrollTop) >= threshold) {
          reveal(button);
          window.removeEventListener('scroll', onScroll);
        }
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return;
    }

    reveal(button);
  }

  function init() {
    var launchers = document.querySelectorAll(SELECTOR);

    launchers.forEach(initAppear);
    hideIfUnavailable();

    // #smile-ui-container is injected on demand, so keep looking for it for a while
    if (!watchPanelState()) {
      var watchStart = Date.now();

      (function pollWatch() {
        if (watchPanelState()) return;

        if (Date.now() - watchStart < MAX_WAIT_MS) {
          setTimeout(pollWatch, POLL_MS);
        }
      })();
    }

    if (launchers.length && launchers[0].getAttribute('data-wt-hide-app-launcher') === 'true') {
      watchAppLauncher();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
